import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Trash2, Network, Loader2, ArrowLeft, AlertTriangle } from 'lucide-react';
import { useStore } from '../../store/useStore';
import { useShallow } from 'zustand/react/shallow';
import { canCreateEntityType } from '../../lib/capabilities';
import { useUserTier } from '../../hooks/useUserTier';
import { useCanCreate } from '../../hooks/useCanCreate';
import { useGoalProgress } from '../../hooks/useGoalProgress';
import { cn } from '../../utils';
import { showToast } from '../../utils/toast';
import { trackEvent } from '../../lib/analytics';
import {
  entityRelationsService,
  type DbRelationRow,
} from '../../services/entity-relations.service';
import {
  entityContactsService,
  type EntityContactLink,
} from '../../services/entity-contacts.service';
import { ROUTES } from '../../routes';
import { supabase } from '../../lib/supabase';
import { completeGoalAndChildren } from '../../services/goal.service';
import { LimitModal } from '../LimitModal';
import { useTranslation } from 'react-i18next';
import type { EntityType } from '../../types';

import {
  ENTITY_MODAL_TYPES,
  entityTypeToModalKey,
  modalKeyToEntityType,
  type ModalEntityKey,
} from './entity-modal.constants';
import { ENTITY_ICONS } from './EntityModalComponents';
import { TypeSelector } from './TypeSelector';
import { TaskForm } from './TaskForm';
import { NoteForm } from './NoteForm';
import { ObjectiveForm } from './ObjectiveForm';
import { EventForm } from './EventForm';
import { ContactForm } from './ContactForm';
import { EntityModalSidebar } from './EntityModalSidebar';

// ============================================================
// HEADER LABEL MAPS
// ============================================================

const CREATE_PREFIX: Record<ModalEntityKey, string> = {
  task: 'entity.create_prefix_task',
  note: 'entity.create_prefix_note',
  objective: 'entity.create_prefix_goal',
  event: 'entity.create_prefix_event',
  contact: 'entity.create_prefix_contact',
};

const EDIT_PREFIX: Record<ModalEntityKey, string> = {
  task: 'entity.edit_prefix_task',
  note: 'entity.edit_prefix_note',
  objective: 'entity.edit_prefix_goal',
  event: 'entity.edit_prefix_event',
  contact: 'entity.edit_prefix_contact',
};

// VB-030 — Label maps for semantic relations
const RELATION_LABELS: Record<string, string> = {
  related_to: 'common.link_to',
  depends_on: 'common.depends_on',
  mentions: 'common.mentions',
  same_topic: 'common.same_topic',
  prerequisite: 'common.prerequisite',
};

const ENTITY_TYPE_LABELS: Record<string, string> = {
  task: 'common.type_task',
  goal: 'common.type_goal',
  note: 'common.type_note',
  event: 'common.type_event',
  contact: 'common.type_contact',
};

// ============================================================
// ORCHESTRATOR
// ============================================================

export function EntityModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tier = useUserTier();
  const {
    modalState,
    closeModal,
    openModal,
    addEntity,
    updateEntity,
    deleteEntity,
    domains,
    goals,
    tasks,
    events,
    notes,
    contacts,
    addToCanvas,
    removeFromCanvas,
    setPendingCompletedGoalId,
  } = useStore(
    useShallow((state) => ({
      modalState: state.modalState,
      closeModal: state.closeModal,
      openModal: state.openModal,
      addEntity: state.addEntity,
      updateEntity: state.updateEntity,
      deleteEntity: state.deleteEntity,
      domains: state.domains,
      goals: state.goals,
      tasks: state.tasks,
      events: state.events,
      notes: state.notes,
      contacts: state.contacts,
      addToCanvas: state.addToCanvas,
      removeFromCanvas: state.removeFromCanvas,
      setPendingCompletedGoalId: state.setPendingCompletedGoalId,
    }))
  );

  const { isOpen, mode, entityType: initialType, entityId, initialData } = modalState;

  const [type, setType] = useState<EntityType>(initialType || 'task');
  const [view, setView] = useState<'selector' | 'form'>('selector');
  const [tagInput, setTagInput] = useState('');
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [localTags, setLocalTags] = useState<string[]>([]);
  const [formData, setFormData] = useState<any>({});
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showCanvasConfirm, setShowCanvasConfirm] = useState(false);

  const mouseDownOnOverlayRef = useRef(false);
  const { checkCanCreate } = useCanCreate();
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitModalProps, setLimitModalProps] = useState({ title: '', message: '' });

  // VB-030 — Semantic relations loaded from entity_relations
  const [semanticRelations, setSemanticRelations] = useState<DbRelationRow[]>([]);

  // VB-033 — Universal entity_contacts
  const [entityContactLinks, setEntityContactLinks] = useState<EntityContactLink[]>([]);
  const [reverseEntityLinks, setReverseEntityLinks] = useState<
    Array<{ entityId: string; entityType: string; label: string }>
  >([]);

  // Bloc 2e — Original data ref for FK change detection
  const originalDataRef = useRef<any>(null);

  // Bloc 2e — Link modification warning
  const [showLinkWarning, setShowLinkWarning] = useState(false);
  const [linkWarningMessage, setLinkWarningMessage] = useState('');
  const [showGoalAchievedConfirm, setShowGoalAchievedConfirm] = useState(false);

  const isFromCanvas = initialData?.fromCanvas === true;

  // ─── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setIsConfirmingDelete(false);
      setIsSubmitting(false);
      setSubmitError(null);
      setShowCanvasConfirm(false);
      setShowLinkWarning(false);
      setLinkWarningMessage('');
      setShowGoalAchievedConfirm(false);
      if (mode === 'edit' && entityId && initialType) {
        const storeSnap = useStore.getState();
        const key = `${initialType}s` as keyof typeof storeSnap;
        const item = (storeSnap[key] as any[])?.find((i) => i.id === entityId);
        if (item) {
          setType(initialType);
          setFormData(item);
          setLocalTags(item.tags || []);
          setTagInput('');
          setIsAddingTag(false);
          setView('form');
          originalDataRef.current = { ...item };
        }
      } else {
        const resolvedType = initialType || 'task';
        setType(resolvedType as EntityType);
        setTagInput('');
        setIsAddingTag(false);
        setLocalTags([]);
        setFormData({
          domainId: domains[0]?.id,
          status: 'todo',
          priority: 'medium',
          date: new Date().toISOString().split('T')[0],
          ...initialData,
        });
        // If we have an initialType (e.g. from createLinked), go straight to form
        setView(initialType ? 'form' : 'selector');
      }
    }
  }, [isOpen, mode, initialType, entityId, initialData]);

  // VB-030 — Load semantic relations
  useEffect(() => {
    if (!isOpen || mode !== 'edit' || !entityId) {
      setSemanticRelations([]);
      return;
    }
    entityRelationsService
      .getRelationsForEntity(entityId)
      .then((rows) => {
        // Resolve labels inline
        const enriched = rows.map((rel) => {
          const isSource = rel.source_id === entityId;
          const otherType = isSource ? rel.target_type : rel.source_type;
          const otherId = isSource ? rel.target_id : rel.source_id;
          const label = resolveEntityLabel(otherType, otherId);
          return { ...rel, _resolvedLabel: label };
        });
        setSemanticRelations(enriched as any);
      })
      .catch(() => setSemanticRelations([]));
  }, [isOpen, mode, entityId]);

  // VB-033 — Load entity contacts / reverse lookup
  useEffect(() => {
    if (!isOpen || mode !== 'edit' || !entityId) {
      setEntityContactLinks([]);
      setReverseEntityLinks([]);
      return;
    }

    if (type !== 'contact') {
      entityContactsService
        .getContactsForEntity(entityId)
        .then((links) => setEntityContactLinks(links))
        .catch(() => setEntityContactLinks([]));
    }

    if (type === 'contact') {
      entityContactsService
        .getEntitiesForContact(entityId)
        .then((links) => {
          const resolved = links.map((link) => {
            const label = resolveEntityLabel(link.entityType, link.entityId);
            return {
              entityId: link.entityId,
              entityType: link.entityType,
              label: label || t('common.untitled'),
            };
          });
          setReverseEntityLinks(resolved);
        })
        .catch(() => setReverseEntityLinks([]));
    }
  }, [isOpen, mode, entityId, type]);

  // ─── Escape key ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeModal]);

  // ─── Guard: check global node limit on create open ─────────────────────────
  useEffect(() => {
    if (!isOpen || mode !== 'create') return;

    (async () => {
      const result = await checkCanCreate('task'); // any type works for global limit
      if (result.reason === 'node_limit') {
        setLimitModalProps({
          title: t('entity.limit_title'),
          message: result.message!,
        });
        setShowLimitModal(true);
        closeModal();
      }
    })();
  }, [isOpen, mode]);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  };

  // ─── Goal force-completion (Behaviour B) ─────────────────────────────────

  const handleGoalConfirmAchieved = async () => {
    if (!entityId) return;
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return;
    try {
      const { completedTaskIds, completedGoalIds } = await completeGoalAndChildren(
        entityId,
        user.id,
        tasks,
        goals
      );
      const now = new Date().toISOString();
      useStore.setState((state) => ({
        tasks: state.tasks.map((tk) =>
          completedTaskIds.includes(tk.id)
            ? { ...tk, status: 'done' as const, completedAt: now }
            : tk
        ),
        goals: state.goals.map((g) =>
          completedGoalIds.includes(g.id)
            ? { ...g, status: 'achieved' as const, completedAt: now }
            : g
        ),
      }));
      // Sync formData so the form saves correctly on submit
      handleChange('status', 'achieved');
      setPendingCompletedGoalId(entityId);
    } catch (e) {
      console.error('[EntityModal] completeGoalAndChildren failed:', e);
      showToast(t('entity.goal_mark_error'), 'error');
    }
  };

  const handleConfirmGoalAchieved = async () => {
    setShowGoalAchievedConfirm(false);
    await handleGoalConfirmAchieved();
    await doSave();
  };

  // ─── Bloc 2e — FK change helpers ──────────────────────────────────────────

  /** Returns FK fields relevant for edge sync depending on entity type */
  const getFkChanges = () => {
    const original = originalDataRef.current;
    if (!original || !entityId) return [];

    const fields: { field: string; resolveName: (id: string) => string }[] = [];
    if (type === 'task') {
      fields.push(
        {
          field: 'goalId',
          resolveName: (id) => goals.find((g) => g.id === id)?.title || t('common.type_goal'),
        },
        {
          field: 'eventId',
          resolveName: (id) => events.find((e) => e.id === id)?.title || t('common.type_event'),
        }
      );
    } else if (type === 'event') {
      fields.push({
        field: 'goalId',
        resolveName: (id) => goals.find((g) => g.id === id)?.title || t('common.type_goal'),
      });
    } else if (type === 'goal') {
      fields.push({
        field: 'parentGoalId',
        resolveName: (id) => goals.find((g) => g.id === id)?.title || t('entity.goal_parent'),
      });
    }

    const changes: Array<{
      field: string;
      oldId: string | null;
      newId: string | null;
      oldName: string;
      newName: string;
    }> = [];

    for (const { field, resolveName } of fields) {
      const oldVal = original[field] || null;
      const newVal = formData[field] || null;
      if (oldVal !== newVal) {
        changes.push({
          field,
          oldId: oldVal,
          newId: newVal,
          oldName: oldVal ? resolveName(oldVal) : '',
          newName: newVal ? resolveName(newVal) : '',
        });
      }
    }
    return changes;
  };

  /** Check if any FK change impacts a canvas edge — returns warning message or null */
  const checkFkChangeWarning = (): string | null => {
    if (mode !== 'edit' || !entityId) return null;
    const { nodes, edges } = useStore.getState();
    const currentNode = nodes.find((n) => n.data.entityId === entityId);
    if (!currentNode) return null;

    const changes = getFkChanges();
    for (const change of changes) {
      if (change.oldId && !change.newId) {
        // Link removed — check if edge exists
        const oldNode = nodes.find((n) => n.data.entityId === change.oldId);
        if (oldNode) {
          const hasEdge = edges.some(
            (e) =>
              (e.source === currentNode.id && e.target === oldNode.id) ||
              (e.source === oldNode.id && e.target === currentNode.id)
          );
          if (hasEdge) {
            return t('entity.fk_warning_remove', { oldName: change.oldName });
          }
        }
      }
      if (change.oldId && change.newId && change.oldId !== change.newId) {
        // Link changed — check if old edge exists
        const oldNode = nodes.find((n) => n.data.entityId === change.oldId);
        if (oldNode) {
          const hasEdge = edges.some(
            (e) =>
              (e.source === currentNode.id && e.target === oldNode.id) ||
              (e.source === oldNode.id && e.target === currentNode.id)
          );
          if (hasEdge) {
            return t('entity.fk_warning_change', {
              oldName: change.oldName,
              newName: change.newName,
            });
          }
        }
      }
    }
    return null;
  };

  /** After save succeeds, update edges on canvas to match FK changes */
  const syncEdgesAfterSave = () => {
    if (!entityId) return;
    const { nodes, edges } = useStore.getState();
    const currentNode = nodes.find((n) => n.data.entityId === entityId);
    if (!currentNode) return;

    const changes = getFkChanges();
    if (changes.length === 0) return;

    let newEdges = [...edges];

    for (const change of changes) {
      // Remove old edge
      if (change.oldId) {
        const oldNode = nodes.find((n) => n.data.entityId === change.oldId);
        if (oldNode) {
          newEdges = newEdges.filter(
            (e) =>
              !(
                (e.source === currentNode.id && e.target === oldNode.id) ||
                (e.source === oldNode.id && e.target === currentNode.id)
              )
          );
        }
      }
      // Add new edge if both nodes are on canvas
      if (change.newId) {
        const newNode = nodes.find((n) => n.data.entityId === change.newId);
        if (newNode) {
          const alreadyExists = newEdges.some(
            (e) =>
              (e.source === currentNode.id && e.target === newNode.id) ||
              (e.source === newNode.id && e.target === currentNode.id)
          );
          if (!alreadyExists) {
            newEdges.push({
              id: `e-${currentNode.id}-${newNode.id}`,
              source: currentNode.id,
              target: newNode.id,
              type: 'waypoint',
              data: { waypoints: [] },
              style: { stroke: '#52525b' },
            });
          }
        }
      }
    }

    useStore.setState({ edges: newEdges });
  };

  // ─── Save logic (shared by direct submit and confirmed link change) ───────

  const doSave = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    // Validation : interdire les événements dans le passé (création uniquement)
    if (mode === 'create' && type === 'event') {
      const eventDate = formData.date;
      const eventTime = formData.time || '00:00';
      if (eventDate) {
        const eventDateTime = new Date(`${eventDate}T${eventTime}:00`);
        const now = new Date();
        if (eventDateTime < now) {
          setSubmitError(t('entity.event_past_error'));
          setIsSubmitting(false);
          return;
        }
      }
    }

    const finalData = { ...formData, tags: localTags };

    try {
      if (mode === 'create') {
        await addEntity(type, finalData);
        trackEvent('entity_created', { type });
      } else if (entityId) {
        await updateEntity(type, entityId, finalData);
        // Bloc 2e — Sync canvas edges after successful save
        syncEdgesAfterSave();
      }
      if (modalState.onSuccess) {
        modalState.onSuccess();
      }
      closeModal();
    } catch (err: any) {
      setSubmitError(err.message || t('entity.save_error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Goal achieved intercept — show confirmation before cascading to tasks/sub-goals
    if (
      type === 'goal' &&
      mode === 'edit' &&
      formData.status === 'achieved' &&
      originalDataRef.current?.status !== 'achieved'
    ) {
      setShowGoalAchievedConfirm(true);
      return;
    }

    // Bloc 2e — Check for FK changes impacting canvas edges before saving
    if (mode === 'edit' && entityId) {
      const warning = checkFkChangeWarning();
      if (warning) {
        setLinkWarningMessage(warning);
        setShowLinkWarning(true);
        return;
      }
    }

    await doSave();
  };

  const handleConfirmLinkChange = async () => {
    setShowLinkWarning(false);
    await doSave();
  };

  const handleDelete = () => {
    if (!isConfirmingDelete) {
      setIsConfirmingDelete(true);
      return;
    }
    if (entityId) {
      if (isFromCanvas) {
        const node = useStore.getState().nodes.find((n) => n.data.entityId === entityId);
        if (node) {
          removeFromCanvas(node.id);
        }
      } else {
        deleteEntity(type, entityId);
      }
      closeModal();
    }
  };

  const handleNavigate = (targetType: EntityType, targetId: string) => {
    openModal('edit', targetType, targetId);
  };

  const handleCreateLinked = (targetType: EntityType, data: any) => {
    openModal('create', targetType, undefined, data);
  };

  const handleUnlink = async (
    e: React.MouseEvent,
    targetType: EntityType,
    targetId: string,
    foreignKeyField: string,
    isOwnedByCurrent: boolean
  ) => {
    e.stopPropagation();
    if (isOwnedByCurrent) {
      handleChange(foreignKeyField, null);
    } else {
      try {
        await updateEntity(targetType, targetId, { [foreignKeyField]: null });
      } catch (err) {
        console.error('Erreur lors de la dissociation:', err);
      }
    }
  };

  const handleAddTag = (tag: string) => {
    const newTag = tag.trim().replace(/^#/, '');
    if (newTag) {
      setLocalTags((prev) => {
        if (prev.includes(newTag)) return prev;
        return [...prev, newTag];
      });
    }
  };

  const removeTag = (tagToRemove: string) => {
    setLocalTags((prev) => prev.filter((tk) => tk !== tagToRemove));
  };

  const resolveEntityLabel = (entityType: string, id: string): string | null => {
    switch (entityType) {
      case 'task':
        return tasks.find((tk) => tk.id === id)?.title ?? null;
      case 'goal':
        return goals.find((g) => g.id === id)?.title ?? null;
      case 'note':
        return notes.find((n) => n.id === id)?.title ?? null;
      case 'event':
        return events.find((e) => e.id === id)?.title ?? null;
      case 'contact':
        return contacts.find((c) => c.id === id)?.name ?? null;
      default:
        return null;
    }
  };

  // VB-033 — Entity contacts handlers
  const handleLinkContact = async (contactId: string) => {
    if (!entityId) return;
    try {
      await entityContactsService.linkContact(entityId, type, contactId);
      const links = await entityContactsService.getContactsForEntity(entityId);
      setEntityContactLinks(links);
    } catch (err) {
      console.error('Erreur liaison contact:', err);
    }
  };

  const handleUnlinkContact = async (contactId: string) => {
    if (!entityId) return;
    try {
      if (type === 'contact') {
        // Reverse: entityId is the contact, contactId is actually the other entity's id
        await entityContactsService.unlinkContact(contactId, entityId);
        setReverseEntityLinks((prev) => prev.filter((l) => l.entityId !== contactId));
      } else {
        await entityContactsService.unlinkContact(entityId, contactId);
        setEntityContactLinks((prev) => prev.filter((l) => l.contactId !== contactId));
      }
    } catch (err) {
      console.error('Erreur dissociation contact:', err);
    }
  };

  const handleQuickCreateContact = async (name: string, role: string) => {
    try {
      const canCreate = await checkCanCreate('contact');
      if (!canCreate.allowed) {
        if (canCreate.reason === 'sub_limit') {
          showToast(canCreate.message!, 'warning');
        } else if (canCreate.reason === 'node_limit') {
          setLimitModalProps({
            title: t('entity.limit_title'),
            message: canCreate.message!,
          });
          setShowLimitModal(true);
        }
        return;
      }
      await addEntity('contact', { name, role });
      const freshContacts = useStore.getState().contacts;
      const newContact = freshContacts.find((c) => c.name === name);
      if (newContact && entityId) {
        await entityContactsService.linkContact(entityId, type, newContact.id);
        const links = await entityContactsService.getContactsForEntity(entityId);
        setEntityContactLinks(links);
      }
    } catch (err) {
      console.error('Erreur création rapide contact:', err);
    }
  };

  const handleAdvancedCreateContact = () => {
    if (!entityId) return;
    const returnEntityType = type;
    const returnEntityId = entityId;
    const existingContactIds = new Set(useStore.getState().contacts.map((c) => c.id));

    openModal('create', 'contact', undefined, {}, async () => {
      const freshContacts = useStore.getState().contacts;
      const newContact = freshContacts.find((c) => !existingContactIds.has(c.id));
      if (newContact) {
        await entityContactsService.linkContact(returnEntityId, returnEntityType, newContact.id);
      } else {
        console.warn('Aucun nouveau contact détecté après création avancée');
      }
      setTimeout(() => {
        openModal('edit', returnEntityType as EntityType, returnEntityId);
      }, 100);
    });
  };

  // ─── Computed relations ────────────────────────────────────────────────────

  const linkedGoal = formData.goalId ? goals.find((g) => g.id === formData.goalId) : null;
  const linkedEvent = formData.eventId ? events.find((e) => e.id === formData.eventId) : null;
  const linkedTask = formData.parentId
    ? tasks.find((tk) => tk.id === formData.parentId)
    : formData.taskId
      ? tasks.find((tk) => tk.id === formData.taskId)
      : null;

  const subTasks = type === 'task' ? tasks.filter((tk) => tk.parentId === entityId) : [];
  const relatedTasks =
    type === 'goal'
      ? tasks.filter((tk) => tk.goalId === entityId)
      : type === 'event'
        ? tasks.filter((tk) => tk.eventId === entityId)
        : [];
  const relatedNotes = ['task', 'goal', 'event'].includes(type)
    ? notes.filter((n) => n.taskId === entityId || n.goalId === entityId || n.eventId === entityId)
    : [];
  const linkedContacts = entityContactLinks
    .map((link) => contacts.find((c) => c.id === link.contactId))
    .filter((c): c is (typeof contacts)[number] => !!c);

  const relatedEvents =
    type === 'contact'
      ? events.filter((e) => formData.eventIds?.includes(e.id) || e.contactIds?.includes(entityId!))
      : [];

  // Bloc 2e — Hierarchical goal progress (replaces direct-only count)
  const goalProgress = useGoalProgress(type === 'goal' && entityId ? entityId : '');
  const relatedTaskCount = {
    done: goalProgress.completedTasks,
    total: goalProgress.totalTasks,
  };

  // Bloc 2e — Sub-goals for goal modal sidebar
  const subGoals =
    type === 'goal' && entityId ? goals.filter((g) => g.parentGoalId === entityId) : [];

  // Bloc 2e — Events linked to goal for goal modal sidebar (with tasks)
  const goalEvents =
    type === 'goal' && entityId
      ? events
          .filter((e) => e.goalId === entityId)
          .map((e) => ({
            ...e,
            eventTasks: tasks.filter((tk) => tk.eventId === e.id),
          }))
      : [];

  // Bloc 2e — Indirect goal for task modal sidebar (task → event → goal)
  const indirectGoal = (() => {
    if (type !== 'task' || !formData.eventId) return null;
    const evt = events.find((e) => e.id === formData.eventId);
    if (!evt || !evt.goalId) return null;
    const goal = goals.find((g) => g.id === evt.goalId);
    if (!goal) return null;
    return { goal: { id: goal.id, title: goal.title }, viaEvent: { id: evt.id, title: evt.title } };
  })();

  // ─── Type selector handler ────────────────────────────────────────────────

  const handleTypeSelect = async (key: ModalEntityKey) => {
    const entityType = modalKeyToEntityType(key) as EntityType;

    // Check sub-limit before opening the form
    const result = await checkCanCreate(entityType as Parameters<typeof checkCanCreate>[0]);
    if (result.reason === 'sub_limit') {
      showToast(result.message!, 'warning');
      return;
    }

    setType(entityType);
    // Reset default status for goals
    if (entityType === 'goal') {
      setFormData((prev: any) => ({ ...prev, status: 'active' }));
    }
    setView('form');
  };

  // ─── Modal key & config ───────────────────────────────────────────────────

  const modalKey = entityTypeToModalKey(type);
  const config = ENTITY_MODAL_TYPES[modalKey];
  const Icon = ENTITY_ICONS[modalKey];

  // ─── Dynamic width ────────────────────────────────────────────────────────

  const widthClass = view === 'selector' ? 'max-w-md' : mode === 'edit' ? 'max-w-3xl' : 'max-w-lg';

  // ─── Form component mapping ───────────────────────────────────────────────

  const formProps = {
    isEdit: mode === 'edit',
    formData,
    onChange: handleChange,
    domains: domains.map((d) => ({ id: d.id, name: d.name })),
    goals: goals.map((g) => ({ id: g.id, title: g.title })),
    tasks: tasks.map((tk) => ({ id: tk.id, title: tk.title, parentId: tk.parentId ?? null })),
    events: events.map((e) => ({ id: e.id, title: e.title })),
    entityId,
    tags: localTags,
    onAddTag: handleAddTag,
    onRemoveTag: removeTag,
  };

  const renderForm = () => {
    switch (type) {
      case 'task':
        return <TaskForm {...formProps} />;
      case 'note':
        return <NoteForm {...formProps} />;
      case 'goal':
        return <ObjectiveForm {...formProps} relatedTaskCount={relatedTaskCount} />;
      case 'event':
        return <EventForm {...formProps} />;
      case 'contact':
        return <ContactForm {...formProps} />;
      case 'habit':
        // Preserve habit form inline for backward compat
        return (
          <div className="flex flex-col gap-4">
            <input
              type="text"
              required
              placeholder={t('entity.habit_name_placeholder')}
              value={formData.name || ''}
              onChange={(e) => handleChange('name', e.target.value)}
              className="w-full bg-transparent border-none text-lg font-semibold text-zinc-100 outline-none placeholder:text-zinc-600 tracking-tight"
            />
            <div>
              <label className="block text-[11px] font-medium text-zinc-400 mb-1.5">
                {t('entity.habit_frequency')}
              </label>
              <select
                value={formData.frequency || 'daily'}
                onChange={(e) => handleChange('frequency', e.target.value)}
                className="w-full bg-zinc-900/40 border border-zinc-600/45 rounded-[10px] px-3 py-2.5 text-[13px] text-zinc-200 focus:border-violet-500/50 focus:bg-zinc-900/60 outline-none transition"
              >
                <option value="daily">{t('entity.habit_daily')}</option>
                <option value="weekly">{t('entity.habit_weekly')}</option>
                <option value="monthly">{t('entity.habit_monthly')}</option>
              </select>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // ─── Filtered TypeSelector ────────────────────────────────────────────────

  const handleFilteredTypeSelect = (key: ModalEntityKey) => {
    const entityType = modalKeyToEntityType(key) as EntityType;
    if (!canCreateEntityType(tier, entityType)) return;
    handleTypeSelect(key);
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  if (!isOpen) {
    return (
      <LimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        title={limitModalProps.title}
        message={limitModalProps.message}
      />
    );
  }

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4"
        onMouseDown={(e) => {
          mouseDownOnOverlayRef.current = e.target === e.currentTarget;
        }}
        onMouseUp={(e) => {
          if (mouseDownOnOverlayRef.current && e.target === e.currentTarget) {
            closeModal();
          }
          mouseDownOnOverlayRef.current = false;
        }}
      >
        <div
          className={cn(
            'bg-zinc-900 border border-zinc-600/40 rounded-2xl shadow-2xl shadow-black/60 w-full overflow-hidden flex flex-col max-h-[85vh] transition-all duration-300',
            widthClass
          )}
        >
          {view === 'selector' ? (
            /* ─── Type Selector ─────────────────────────────────────── */
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <span className="text-[15px] font-semibold text-zinc-200">
                  {t('entity.create_element')}
                </span>
                <button
                  type="button"
                  onClick={closeModal}
                  className="p-1.5 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 rounded-lg transition-colors cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <TypeSelector onSelect={handleFilteredTypeSelect} />
            </div>
          ) : (
            <>
              {/* ─── Header ──────────────────────────────────────────── */}
              <div className="px-5 py-3.5 border-b border-zinc-700/30 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  {mode === 'create' && (
                    <button
                      type="button"
                      onClick={() => setView('selector')}
                      className="text-zinc-500 hover:text-zinc-300 p-1 cursor-pointer transition-colors"
                    >
                      <ArrowLeft size={18} />
                    </button>
                  )}
                  <div
                    className="w-[30px] h-[30px] rounded-[9px] flex items-center justify-center"
                    style={{
                      background: `linear-gradient(135deg, ${config.accentSoft}, ${config.accentBorder})`,
                      color: config.accent,
                    }}
                  >
                    <Icon size={14} />
                  </div>
                  <span className="text-[14px] font-semibold text-zinc-200">
                    {mode === 'edit' ? t(EDIT_PREFIX[modalKey]) : t(CREATE_PREFIX[modalKey])}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  {mode === 'edit' && ['task', 'note', 'goal', 'event'].includes(type) && (
                    <button
                      type="button"
                      onClick={() => {
                        const exists = useStore
                          .getState()
                          .nodes.some((n) => n.data.entityId === entityId);
                        if (exists) {
                          addToCanvas(entityId!, type);
                          useStore.getState().setFocusedEntityId(entityId!);
                          closeModal();
                          navigate(ROUTES.brain);
                        } else {
                          setShowCanvasConfirm(true);
                        }
                      }}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-[7px] text-[11px] font-medium cursor-pointer transition-colors"
                      style={{
                        backgroundColor: `${config.accent}1e`,
                        border: `1px solid ${config.accent}4d`,
                        color: config.accent,
                      }}
                      title={t('entity.visual_brain')}
                    >
                      <Network size={12} />
                      Visual Brain
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={closeModal}
                    className="p-1 text-zinc-600 hover:text-zinc-300 cursor-pointer transition-colors"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>

              {/* ─── Canvas confirmation banner ─────────────────────── */}
              {showCanvasConfirm && (
                <div className="mx-4 mt-3 p-3 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-between gap-3">
                  <p className="text-sm text-blue-300">{t('entity.add_to_canvas_message')}</p>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => setShowCanvasConfirm(false)}
                      className="px-3 py-1.5 text-xs font-medium text-zinc-400 hover:text-zinc-200 transition-colors cursor-pointer"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        addToCanvas(entityId!, type);
                        useStore.getState().setFocusedEntityId(entityId!);
                        closeModal();
                        navigate(ROUTES.brain);
                      }}
                      className="px-3 py-1.5 text-xs font-medium bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors cursor-pointer"
                    >
                      {t('entity.add_and_view')}
                    </button>
                  </div>
                </div>
              )}

              {/* ─── Error banner ────────────────────────────────────── */}
              {submitError && (
                <div className="mx-5 mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                  {submitError}
                </div>
              )}

              {/* ─── Body ───────────────────────────────────────────── */}
              <div className="flex-1 overflow-hidden flex">
                <form
                  id="entity-form"
                  onSubmit={handleSubmit}
                  className="flex-1 overflow-y-auto px-5 py-[18px]"
                >
                  {renderForm()}
                </form>

                {mode === 'edit' && entityId && (
                  <EntityModalSidebar
                    entityType={type}
                    entityId={entityId}
                    formData={formData}
                    linkedGoal={linkedGoal ? { id: linkedGoal.id, title: linkedGoal.title } : null}
                    linkedEvent={
                      linkedEvent ? { id: linkedEvent.id, title: linkedEvent.title } : null
                    }
                    linkedTask={linkedTask ? { id: linkedTask.id, title: linkedTask.title } : null}
                    subTasks={subTasks.map((tk) => ({
                      id: tk.id,
                      title: tk.title,
                      status: tk.status,
                    }))}
                    relatedTasks={relatedTasks.map((tk) => ({
                      id: tk.id,
                      title: tk.title,
                      status: tk.status,
                    }))}
                    relatedNotes={relatedNotes.map((n) => ({ id: n.id, title: n.title }))}
                    relatedEvents={relatedEvents.map((e) => ({ id: e.id, title: e.title }))}
                    semanticRelations={semanticRelations}
                    onNavigate={handleNavigate}
                    onCreateLinked={handleCreateLinked}
                    onUnlink={handleUnlink}
                    entityContactLinks={entityContactLinks}
                    linkedContacts={linkedContacts.map((c) => ({ id: c.id, name: c.name }))}
                    allContacts={contacts.map((c) => ({
                      id: c.id,
                      name: c.name,
                      role: c.role ?? undefined,
                    }))}
                    onLinkContact={handleLinkContact}
                    onUnlinkContact={handleUnlinkContact}
                    onQuickCreateContact={handleQuickCreateContact}
                    onAdvancedCreateContact={handleAdvancedCreateContact}
                    reverseEntityLinks={type === 'contact' ? reverseEntityLinks : undefined}
                    subGoals={subGoals.map((g) => ({ id: g.id, title: g.title, status: g.status }))}
                    goalEvents={goalEvents.map((e) => ({
                      id: e.id,
                      title: e.title,
                      tasks: e.eventTasks.map((tk) => ({
                        id: tk.id,
                        title: tk.title,
                        status: tk.status,
                      })),
                    }))}
                    indirectGoal={indirectGoal}
                  />
                )}
              </div>

              {/* ─── Footer ─────────────────────────────────────────── */}
              <div className="px-5 py-3 border-t border-zinc-700/30 flex items-center justify-between">
                {mode === 'edit' ? (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleDelete}
                      className={cn(
                        'flex items-center gap-1.5 px-3 py-2 rounded-lg transition-colors text-[12px] font-medium cursor-pointer',
                        isConfirmingDelete
                          ? 'bg-red-500 text-white hover:bg-red-600'
                          : 'text-red-400 hover:text-red-300 hover:bg-red-400/10'
                      )}
                    >
                      <Trash2 size={13} />
                      {isConfirmingDelete
                        ? isFromCanvas
                          ? t('entity.confirm_remove')
                          : t('entity.confirm_delete')
                        : isFromCanvas
                          ? t('entity.remove_from_canvas')
                          : t('common.delete')}
                    </button>
                    {isConfirmingDelete && (
                      <button
                        type="button"
                        onClick={() => setIsConfirmingDelete(false)}
                        className="px-3 py-2 text-[12px] font-medium text-zinc-400 hover:text-zinc-200 transition-colors cursor-pointer"
                      >
                        Annuler
                      </button>
                    )}
                  </div>
                ) : (
                  <div />
                )}

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 rounded-lg text-[12px] font-medium text-zinc-500 hover:text-zinc-200 transition-colors cursor-pointer"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    form="entity-form"
                    disabled={isSubmitting}
                    className="px-[22px] py-2 rounded-[9px] text-[12px] font-semibold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 cursor-pointer"
                    style={{
                      backgroundColor: config.accent,
                      boxShadow: `0 2px 12px ${config.accent}59`,
                    }}
                  >
                    {isSubmitting && <Loader2 size={14} className="animate-spin" />}
                    {isSubmitting
                      ? t('entity.saving')
                      : mode === 'create'
                        ? t('common.create')
                        : t('common.save')}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Goal achieved confirmation modal */}
      {showGoalAchievedConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
          <div className="bg-zinc-900 border border-zinc-600/40 rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-xl bg-green-500/15 border border-green-500/30 flex items-center justify-center">
                <AlertTriangle size={18} className="text-green-400" />
              </div>
              <span className="text-[15px] font-semibold text-zinc-200">
                {t('entity.goal_achieved_title')}
              </span>
            </div>
            <p className="text-[13px] text-zinc-400 leading-relaxed mb-6">
              {t('entity.goal_achieved_confirm')}
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowGoalAchievedConfirm(false)}
                className="px-4 py-2 rounded-lg text-[12px] font-medium text-zinc-500 hover:text-zinc-200 transition-colors cursor-pointer"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleConfirmGoalAchieved}
                disabled={isSubmitting}
                className="px-5 py-2 rounded-[9px] text-[12px] font-semibold bg-green-600 hover:bg-green-700 text-white transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2"
              >
                {isSubmitting && <Loader2 size={14} className="animate-spin" />}
                {t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bloc 2e — Link modification warning modal */}
      {showLinkWarning && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
          <div className="bg-zinc-900 border border-zinc-600/40 rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                <AlertTriangle size={18} className="text-amber-400" />
              </div>
              <span className="text-[15px] font-semibold text-zinc-200">
                {t('entity.link_changes_title')}
              </span>
            </div>
            <p className="text-[13px] text-zinc-400 leading-relaxed mb-6">{linkWarningMessage}</p>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowLinkWarning(false)}
                className="px-4 py-2 rounded-lg text-[12px] font-medium text-zinc-500 hover:text-zinc-200 transition-colors cursor-pointer"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleConfirmLinkChange}
                disabled={isSubmitting}
                className="px-5 py-2 rounded-[9px] text-[12px] font-semibold bg-amber-500 hover:bg-amber-600 text-white transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2"
              >
                {isSubmitting && <Loader2 size={14} className="animate-spin" />}
                {t('entity.confirm_and_save')}
              </button>
            </div>
          </div>
        </div>
      )}

      <LimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        title={limitModalProps.title}
        message={limitModalProps.message}
      />
    </>
  );
}
